/**
 * Shell auth hook — adapts BeHeard's auth hooks to the SuiteShellAdapter.AuthHookResult interface.
 */

import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { selectIsCentralAuthenticated } from '../../store/slices/centralAuthSlice';
import { useWorkspace } from '../../hooks/useWorkspace';

export function useShellAuth() {
  const isAuthenticated = useSelector(selectIsCentralAuthenticated);
  const { switchWorkspace } = useWorkspace();

  // switchWorkspace reports failure as a VALUE ({ success: false, error }). This
  // used to await it and return normally, so the shell closed the switcher as if
  // it had worked and the user saw nothing. A failure is now shown, and thrown,
  // which is the shell's contract for "keep the switcher open" (ui-shell
  // HeaderBar: on reject it logs and leaves the panel open).
  const handleSwitchWorkspace = useCallback(
    async (workspaceId: string) => {
      const result = await switchWorkspace(workspaceId);
      if (!result.success) {
        const message = result.error || "Couldn't switch workspace. Try again.";
        toast.error(message, { id: 'workspace-switch-error' });
        throw new Error(message);
      }
    },
    [switchWorkspace]
  );

  return {
    authSource: isAuthenticated ? ('central' as const) : null,
    switchWorkspace: handleSwitchWorkspace,
  };
}
